import { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import Brand from "../components/Brand";
import CocaColaWelcome from "../components/CocaColaWelcome";
import Modal from "../components/Modal";
import useAuth from "../hooks/useAuth";
import { requestPasswordReset } from "../services/auth.service";
import { useToast } from "../context/ToastContext";
import { getApiErrorMessage } from "../helpers/getApiErrorMessage";
import { FEATURE_FLAGS } from "../config/featureFlags";
/* Enlaces legales ocultos temporalmente en login
import { LEGAL_LIBRARY } from "../constants/legal";
*/
import { version } from "../version";
import { getHeaderTheme } from "../config/headerTheme";

const MainLogin = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0 clamp(12px, 4vw, 24px);
  min-height: 100vh;
`;

const LoginContainer = styled.div`
  width: 100%;
  margin-top: clamp(12px, 4vw, 32px);
  text-align: center;
`;

const LogoWrapper = styled.div`
  margin-bottom: clamp(20px, 4vw, 30px);

  > div {
    min-height: clamp(104px, 16vw, 132px);
  }
`;

const LoginHeader = styled.div`
  width: min(80%, 360px);
  margin: clamp(22px, 4vw, 30px) auto;
  text-align: center;

  h1 {
    font-size: clamp(24px, 3vw, 28px);
    font-weight: 700;
    color: #000;
    margin-bottom: 10px;
  }

  p {
    font-size: clamp(15px, 2vw, 17px);
    line-height: 1.4;
    color: #555;
    margin: 0;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const InputField = styled.input`
  width: 100%;
  border-radius: 6px;
  height: clamp(54px, 7vw, 60px);
  border: 1px solid #afafaf;
  margin-bottom: clamp(16px, 3vw, 25px);
  padding: 0 clamp(16px, 3vw, 20px);
  font-size: 16px;
  outline: none;
`;

const ButtonControls = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 10px;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ButtonRegister = styled.div`
  display: flex;
  justify-content: flex-start;

  button {
    border-radius: 100px;
    color: #111;
    background-color: #f3f3f3;
    border: 1px solid #f3f3f3;
    width: 100%;
    padding: 15px 20px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
      color: white;
      background-color: #000;
      border-color: #000;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
`;

const theme = getHeaderTheme();

const ButtonLogin = styled.div`
  display: flex;
  justify-content: flex-end;

  button {
    border-radius: 100px;
    color: ${theme.text};
    background: ${theme.primary};
    border: 1px solid transparent;
    width: 100%;
    padding: 15px 20px;
    font-size: 16px;
    cursor: pointer;
    transition: filter 0.25s ease;

    &:hover {
      filter: brightness(0.92);
    }

    &:disabled {
      cursor: wait;
      opacity: 0.7;
    }
  }
`;

const ForgotPasswordRow = styled.div`
  text-align: left;
  margin: -8px 0 16px;

  button {
    background: none;
    border: none;
    padding: 0;
    color: #000;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      opacity: 0.7;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const ResetModalBody = styled.div`
  margin-bottom: 20px;

  p {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.5;
    color: #444;
    text-align: center;
  }

  input {
    margin-bottom: 0;
  }
`;

/* Enlaces legales ocultos temporalmente en login
const LegalLinksRow = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  justify-content: center;

  @media (max-width: 480px) {
    gap: 8px 12px;
  }
`;

const LegalAnchor = styled.a`
  color: #555;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #000;
    text-decoration: underline;
  }
`;
*/

const Version = styled.div`
  margin-top: clamp(16px, 4vw, 28px);
  text-align: center;
  color: #afafaf;
  font-size: 14px;
`;

const Brands = styled.div`
  padding: clamp(12px, 3vw, 18px) clamp(0px, 8vw, 50px) clamp(12px, 3vw, 18px);
`;

const LoginPage = (props) => {
  const { login } = useAuth();
  const toast = useToast();
  const history = useHistory();
  const [referrer, setReferrer] = useState("/");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const requestedReferrer = props?.location?.state?.referrer;
    const canReturnToReferrer =
      typeof requestedReferrer === "string" &&
      requestedReferrer !== "/login" &&
      (requestedReferrer.startsWith("/almacen") || requestedReferrer.startsWith("/w/"));

    if (canReturnToReferrer) {
      setReferrer(requestedReferrer);
    }
  }, [props?.location?.state?.referrer]);

  const handleLogin = async () => {
    if (!email) return toast.error("El campo email es requerido");
    if (!password) return toast.error("El campo password es requerido");

    try {
      setIsLoading(true);
      await login({ email, password });
      // Forzar recarga completa para limpiar todo estado residual de sesiones anteriores
      window.location.href = referrer;
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Credenciales no validas"));
      setIsLoading(false);
    }
  };

  const handleRegister = () => {
    if (isLoading) return;
    history.push("/signup/almacen");
  };

  const handleOpenResetModal = () => {
    if (isLoading || isResetting) return;
    setResetEmail(email);
    setShowResetModal(true);
  };

  const handleCloseResetModal = () => {
    if (isResetting) return;
    setShowResetModal(false);
  };

  const handleRequestReset = async () => {
    if (!resetEmail) return toast.error("El campo email es requerido");

    try {
      setIsResetting(true);
      await requestPasswordReset({ email: resetEmail });
      toast.success("Correo de restablecimiento enviado");
      setShowResetModal(false);
      setResetEmail("");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo enviar el correo de restablecimiento"));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Fragment>
      <MainLogin>
        <LoginContainer>
          <LogoWrapper>
            {/* Logo Coca-Cola oculto en login para campaña ALMA */}
            <Brand type="almacen" width="300" height="64" showCokeLogo={false} />
          </LogoWrapper>

          <LoginHeader>
            <h1>Inicia sesión</h1>
            <p>Si ya tienes una cuenta TiendIA, inicia sesión más abajo.</p>
          </LoginHeader>

          <div>
            <InputField
              type="email"
              placeholder="Email"
              disabled={isLoading}
              value={email}
              onChange={(event) => setEmail(event.target.value.toLowerCase())}
            />
            <InputField
              type="password"
              placeholder="Password"
              disabled={isLoading}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !isLoading) handleLogin();
              }}
            />
          </div>

          <ForgotPasswordRow>
            <button type="button" onClick={handleOpenResetModal} disabled={isLoading || isResetting}>
              ¿Olvidaste tu contraseña?
            </button>
          </ForgotPasswordRow>

          <ButtonControls>
            <ButtonRegister>
              <button onClick={handleRegister} disabled={isLoading}>
                Registrarse
              </button>
            </ButtonRegister>
            <ButtonLogin>
              <button onClick={handleLogin} disabled={isLoading}>
                {isLoading ? "Cargando..." : "Ingresar"}
              </button>
            </ButtonLogin>
          </ButtonControls>

          {/* Enlaces legales ocultos temporalmente en login
          <LegalLinksRow>
            {LEGAL_LIBRARY.map((item) => (
              <LegalAnchor key={item.href} href={item.href} target="_blank" rel="noreferrer">
                {item.label}
              </LegalAnchor>
            ))}
          </LegalLinksRow>
          */}

          <Brands>
            <Version>{version}</Version>
          </Brands>

          {FEATURE_FLAGS.SPONSOR_BRANDING_ENABLED && <CocaColaWelcome />}
        </LoginContainer>
      </MainLogin>

      <Modal
        isOpen={showResetModal}
        onClose={handleCloseResetModal}
        label="Recuperar contraseña"
        acceptButtonText={isResetting ? "Enviando..." : "Enviar correo"}
        cancelButtonText="Cancelar"
        onAccept={handleRequestReset}
        onCancel={handleCloseResetModal}
        acceptButtonDisabled={isResetting}
      >
        <ResetModalBody>
          <p>Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.</p>
          <InputField
            type="email"
            placeholder="Email"
            disabled={isResetting}
            value={resetEmail}
            onChange={(event) => setResetEmail(event.target.value.toLowerCase())}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !isResetting) handleRequestReset();
            }}
          />
        </ResetModalBody>
      </Modal>
    </Fragment>
  );
};

export default LoginPage;
